import { useTranslation } from "react-i18next";

import { getSportColor } from "../../workoutStatus";
import type { PlanSessionCard } from "./WeekView";

/**
 * MonthView — Cycle 18 P1.3 canon polish.
 *
 * Mockup: docs/design/claude-design/desktop/athlete/Storica Desktop - Plan.dc.html
 *
 * Canon spec:
 *   • Grid 7 cols × 5 rows, gridAutoRows 90px, gap 6
 *   • Cell padding 8px 9px, borderRadius 7
 *   • Not-in-month: background rgba(10,14,41,.4), no content
 *   • Today: bg rgb(var(--accent-rgb) / .10) + inset 0 0 0 1px rgb(var(--accent-rgb) / .4)
 *   • Race: bg color-mix(in srgb, var(--ok) 8%, transparent) + inset 0 0 0 1px color-mix(in srgb, var(--ok) 30%, transparent)
 *   • Normal: bg #0A0E29 + inset 0 1px 0 rgba(255,255,255,.03)
 *   • Date number top-left: Onest 13/700, color iris (today) / green (race) / INK.b (normal)
 *   • "RACE" label top-right: Mono 8/0.18em weight 700 green
 *   • Inside: sport-color TSS bar (5px tall, width = TSS/2%, max 100%, opacity .85)
 *   • Bottom-right: TSS number Onest 12/700 INK.b + Mono 8 "TSS" 0.14em label
 *   • If sessions empty AND in-month: "—" placeholder (mono 10 INK.d)
 */
export interface MonthDayCell {
  weekday: number;
  date: number;
  /** Полная дата ячейки. День месяца не годится: лист дня спрашивает
      погоду и строит тренировку — обоим нужен год и месяц. */
  iso: string;
  inFocusMonth: boolean;
  sessions: PlanSessionCard[];
  isToday?: boolean;
  isRace?: boolean;
}

export interface MonthViewProps {
  cells: MonthDayCell[];
  weekdayLabels: string[];
  /** «Июль 2026» — заголовок фокус-месяца над сеткой. */
  monthLabel?: string;
  onDayClick?: (cell: MonthDayCell) => void;
  className?: string;
}

// 2026-07-28: своя таблица дисциплин удалена — точный дубль той, что стояла
// в WeekView, с тем же набором отставаний (trail лаймом, gravel цветом бега,
// triathlon цветом вело). Единый источник — SPORT_COLOR в workoutStatus.ts.
const sportColor = getSportColor;

// Pick the dominant sport for the cell's TSS bar — first session's sport.
// Multi-sport days would need a stacked bar; simple version for now.
function dominantSport(sessions: PlanSessionCard[]): string | null {
  if (sessions.length === 0) return null;
  return sessions[0].sport;
}

function sumTss(sessions: PlanSessionCard[]): number {
  return sessions.reduce((acc, s) => acc + (s.tss ?? 0), 0);
}

const COLORS = {
  bgNotInMonth: "var(--bg-base)",
  // Гонка красится --ok, а не лаймом --active-500: соседний WeekView для той
  // же сущности использует классы text-ok / border-ok, то есть два вида
  // одного экрана расходились. Плюс канон-сессия 24.07 сняла лайм с роли
  // статуса, оставив его за disc-run.
  bgRace: "color-mix(in srgb, var(--ok) 8%, transparent)",
  bgToday: "rgb(var(--accent-rgb) / .10)",
  bgNormal: "var(--bg-base)",
  bgBar: "var(--bg-card)",
  shadowRace: "inset 0 0 0 1px color-mix(in srgb, var(--ok) 30%, transparent)",
  shadowToday: "inset 0 0 0 1px rgb(var(--accent-rgb) / .4)",
  shadowNormal: "inset 0 1px 0 rgba(255,255,255,.03)",
  green: "var(--ok)",
  irisL: "var(--accent)",
  inkB: "var(--fg-primary)",
  inkM: "var(--fg-muted)",
  inkD: "var(--fg-dim)",
};

export function MonthView({ cells, weekdayLabels, monthLabel, onDayClick, className = "" }: MonthViewProps) {
  const { t } = useTranslation("plan");

  return (
    <div data-testid="month-view" className={`flex flex-col gap-2 ${className}`}>
      {monthLabel && (
        <div
          data-testid="month-view-title"
          className="text-sm font-bold text-fg-primary capitalize"
        >
          {monthLabel}
        </div>
      )}
      {/* Weekday header row — Mono 9.5/0.18em INK.m */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 6,
        }}
      >
        {weekdayLabels.map((label, idx) => (
          <div
            key={idx}
            className="indigo-label text-center"
            style={{ padding: "8px 0", letterSpacing: "0.18em" }}
          >
            {label}
          </div>
        ))}
      </div>

      {/* 5×7 cells with canon spec */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(7, 1fr)",
          gridAutoRows: "90px",
          gap: 6,
        }}
      >
        {cells.map((cell, idx) => {
          if (!cell.inFocusMonth) {
            return (
              <div
                key={idx}
                data-testid={`month-cell-${idx}`}
                style={{
                  background: COLORS.bgNotInMonth,
                  borderRadius: 7,
                }}
              />
            );
          }

          const tss = sumTss(cell.sessions);
          const sport = dominantSport(cell.sessions);
          const dateColor = cell.isToday ? COLORS.irisL : cell.isRace ? COLORS.green : COLORS.inkB;
          const bg = cell.isRace
            ? COLORS.bgRace
            : cell.isToday
              ? COLORS.bgToday
              : COLORS.bgNormal;
          const shadow = cell.isRace
            ? COLORS.shadowRace
            : cell.isToday
              ? COLORS.shadowToday
              : COLORS.shadowNormal;

          // Кликабелен КАЖДЫЙ день месяца, а не только занятый. Пустой
          // день — это как раз тот, в который что-то ставят: раньше по
          // нему нельзя было даже нажать, и построить тренировку на
          // конкретную дату из календаря было невозможно.
          const clickable = !!onDayClick && cell.inFocusMonth;

          return (
            <div
              key={idx}
              data-testid={`month-cell-${idx}`}
              onClick={clickable ? () => onDayClick!(cell) : undefined}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              /* Метка собиралась из ОДНОГО ключа с параметрами, которых
                 в него не передавали: словарь ждал {{tss}}, компонент
                 слал count, и в озвучку уезжало «нагрузка {{tss}} TSS»
                 буквально. Ключей теперь два, потому что и предложений
                 два: у пустого дня нагрузки нет, и «0 TSS» там звучит
                 как поломка, а не как отдых. */
              aria-label={
                cell.sessions.length === 0
                  ? t("month_view.cell_aria_empty", {
                      date: cell.date,
                      defaultValue: `${cell.date} — ничего не запланировано`,
                    })
                  : t("month_view.cell_aria", {
                      date: cell.date,
                      count: cell.sessions.length,
                      tss: Math.round(
                        cell.sessions.reduce((sum, s) => sum + (s.tss ?? 0), 0),
                      ),
                      defaultValue: `${cell.date} — тренировок: ${cell.sessions.length}`,
                    })
              }
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onDayClick!(cell);
                      }
                    }
                  : undefined
              }
              style={{
                background: bg,
                borderRadius: 7,
                padding: "8px 9px",
                boxShadow: shadow,
                cursor: clickable ? "pointer" : "default",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {/* Top row: date + RACE label */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <span
                  style={{
                    fontFamily: '"Onest", "Geist", system-ui, sans-serif',
                    fontSize: 13,
                    fontWeight: 700,
                    color: dateColor,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {cell.date}
                </span>
                {cell.isRace && (
                  <span
                    style={{
                      fontFamily: '"JetBrains Mono", "ui-monospace", monospace',
                      fontSize: 8,
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      color: COLORS.green,
                    }}
                  >
                    {t("month_view.race", { defaultValue: "RACE" })}
                  </span>
                )}
              </div>

              {/* Body: sport-color TSS bar + TSS number, OR rest placeholder */}
              {tss > 0 ? (
                <div>
                  <div
                    style={{
                      height: 5,
                      background: COLORS.bgBar,
                      borderRadius: 2.5,
                      overflow: "hidden",
                      marginBottom: 4,
                    }}
                  >
                    <div
                      data-testid={`month-cell-tss-bar-${idx}`}
                      style={{
                        height: 5,
                        width: `${Math.min(100, tss / 2)}%`,
                        background: sport ? sportColor(sport) : COLORS.irisL,
                        borderRadius: 2.5,
                        opacity: 0.85,
                      }}
                    />
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span
                      style={{
                        fontFamily: '"Onest", "Geist", system-ui, sans-serif',
                        fontSize: 12,
                        fontWeight: 700,
                        color: COLORS.inkB,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {tss}
                    </span>{" "}
                    <span
                      style={{
                        fontFamily: '"JetBrains Mono", "ui-monospace", monospace',
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        color: COLORS.inkM,
                      }}
                    >
                      TSS
                    </span>
                  </div>
                </div>
              ) : cell.sessions.length > 0 ? (
                /* Плановые сессии без TSS (у импортных планов load пуст) —
                   раньше такие дни выглядели как отдых «—», и месяц
                   показывал только факт. Точки цвета спорта = «план стоит». */
                <div
                  data-testid={`month-cell-planned-${idx}`}
                  style={{
                    display: "flex",
                    gap: 3,
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {cell.sessions.slice(0, 3).map((s, i) => (
                    <span
                      key={i}
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        background: sportColor(s.sport) ?? COLORS.irisL,
                        opacity: 0.85,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "right",
                    fontFamily: '"JetBrains Mono", "ui-monospace", monospace',
                    fontSize: 10,
                    color: COLORS.inkD,
                    letterSpacing: 0,
                  }}
                >
                  —
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
